/* eslint-disable @animech/axiom/no-unrelated-imports */
import {
	ConfigurationValue,
	Image,
	makeUi,
	NodeId,
	ProductId,
	PropertyNode,
	UiStructure,
} from "@animech/axiom-format/src/definitions/definition-bindings";
import {
	ProductListNode as CoreProductListNode,
	RootedProperty,
} from "@animech/axiom-format";
import { cacheBustPaths } from "../utility/cache-bust";
import { Root, root } from "./property-definitions";
import { SIDE_INDICES } from "./type-definitions";
// eslint-disable-next-line @animech/axiom/no-unrelated-imports
import { ExtendedFieldProps as ToggleButtonGroup } from "src/ui/components/widgets/toggle-button-group";
// eslint-disable-next-line @animech/axiom/no-unrelated-imports
import { ExtendedThumbnailDropDownProps } from "src/ui/components/widgets/thumbnail-dropdown";
// eslint-disable-next-line @animech/axiom/no-unrelated-imports
import { ExtendedAccordionFieldProps } from "src/ui/components/widgets/accordion/accordion";
// eslint-disable-next-line @animech/axiom/no-unrelated-imports
import { ConditionalMessageProps } from "src/ui/components/widgets/conditional-message/conditional-message";
// eslint-disable-next-line @animech/axiom/no-unrelated-imports
import { ExtendedHorizontalCardSelectProps } from "src/ui/components/widgets/horizontal-card-select";
// eslint-disable-next-line @animech/axiom/no-unrelated-imports
import { UiNode } from "src/common";

export type AccordionGroupNode = {
	type: "accordionGroup";
	id: NodeId;
	children: CustomNode[];
};
export type AccordionNode = {
	type: "accordion";
	state: RootedProperty<Root>;
	visible?: ConfigurationValue<boolean>;
	children: UiNode[];
} & ExtendedAccordionFieldProps;

export type ColorPickerNode = {
	type: "colorPicker";
	id?: NodeId;
	state: RootedProperty<Root>;
};

export type CustomProductListNode = {
	type: "customProductList";
	draggable?: boolean;
} & Omit<CoreProductListNode<Root, NodeId, ProductId, Image, never>, "type">;

export type ToggleButtonGroupNode = {
	type: "toggleButtonGroup";
	id?: NodeId;
	state: RootedProperty<Root>;
	visible?: ConfigurationValue<boolean>;
} & ToggleButtonGroup;

export type ColorSelectWithColorPropagationNode = Omit<PropertyNode, "type"> & {
	type: "colorSelectWithColorPropagation";
	id?: NodeId;
	state: RootedProperty<Root>;
};

export type DecimalInputNode = {
	id?: NodeId;
	type: "decimalInput";
	state: RootedProperty<Root>;
	visible?: ConfigurationValue<boolean>;
	min?: number;
	max?: number;
};

export type ThumbnailDropDownNode = {
	type: "thumbnailDropDown";
	id?: NodeId;
	state: RootedProperty<Root>;
	visible?: ConfigurationValue<boolean>;
} & ExtendedThumbnailDropDownProps;

export type ConditionalMessageNode = {
	type: "conditionalMessage";
	id?: NodeId;
	state: RootedProperty<Root>;
	visible?: ConfigurationValue<boolean>;
} & ConditionalMessageProps;

export type HorizontalCardSelectNode = {
	type: "horizontalCardSelect";
	id?: NodeId;
	state: RootedProperty<Root>;
	visible?: ConfigurationValue<boolean>;
} & ExtendedHorizontalCardSelectProps;

export type VisibleGroupNode = {
	type: "visibleGroup";
	id?: NodeId;
	visible?: ConfigurationValue<boolean>;
	children: UiNode[];
};

export type CustomNode =
	| AccordionGroupNode
	| AccordionNode
	| CustomProductListNode
	| ColorPickerNode
	| ToggleButtonGroupNode
	| ColorSelectWithColorPropagationNode
	| AccordionGroupNode
	| AccordionNode
	| DecimalInputNode
	| ThumbnailDropDownNode
	| ConditionalMessageNode
	| HorizontalCardSelectNode
	| VisibleGroupNode;

// eslint-disable-next-line @animech/axiom/valid-axiom-system-values
export const images = cacheBustPaths([
	"images/logo/logo.svg",
	"images/logo/logo-inverted.svg",
	"images/favicon/favicon-16x16.png",
	"images/favicon/favicon-32x32.png",
	"images/favicon/favicon-150x150.png",
	"images/favicon/favicon-192x192.png",
	"images/tables/Table-Bovik-150.webp",
	"images/tables/Table-B31-84.webp",
	"images/tables/Table-B31-170.webp",
	"images/tables/Table-B31-230.webp",
	"images/tables/Table-6B-60.webp",
	"images/tables/Table-9A-100.webp",
	"images/tables/Table-9A-120.webp",
	"images/tables/Table-B25A-120.webp",
	"images/tables/Table-HighTech-60.webp",
	"images/tables/Table-HighTech-110.webp",
	"images/tables/Table-Libelle-140.webp",
	"images/tables/Table-Libelle-70.webp",
	"images/chairs/Chair-A2.webp",
	"images/chairs/Chair-HighTech.webp",
	"images/chairs/Chair-Bovik.webp",
	"images/chairs/Chair-Libelle.webp",
	"images/chairs/Chair-1.webp",
	"images/benches/Bench-8.webp",
	"images/benches/Bench-9.webp",
	"images/benches/Bench-Libelle.webp",
	"images/sofas/Sofa-3.webp",
	"images/sofas/Sofa-5.webp",
	"images/themes/theme-none.svg",
	"images/themes/theme-teak-galvanized.svg",
	"images/themes/theme-teak-black.svg",
	"images/themes/theme-oak-galvanized.svg",
	"images/themes/theme-oak-black.svg",
	"images/themes/theme-pine-green.svg",
	"images/themes/theme-white-lacquer-galvanized.svg",
	"images/themes/theme-white-lacquer-black.svg",
	"images/themes/theme-green-galvanized.svg",
	"images/themes/theme-green-black.svg",
	"images/themes/theme-red-galvanized.svg",
	"images/themes/theme-red-black.svg",
	"images/themes/theme-hightech-white-galvanized.svg",
	"images/themes/theme-hightech-white-black.svg",
	"images/themes/theme-hightech-graphite-galvanized.svg",
	"images/themes/theme-hightech-graphite-black.svg",
	"images/themes/theme-hightech-galvanized.svg",
	"images/themes/theme-hightech-powdercoat-black.svg",
	"images/themes/theme-hightech-powdercoat-chestnut.svg",
	"images/themes/theme-hightech-powdercoat-ivory.svg",
	"images/themes/theme-hightech-powdercoat-petrol.svg",
	"images/themes/theme-libelle-green.svg",
	"images/themes/theme-libelle-grey.svg",
	"images/themes/theme-libelle-graphite.svg",
]);

export const nodeIds = [
	"sidebar",
	"sidebar:accordion-group",
	"sidebar:materialTheme",
	"sidebar:table",
	"sidebar:table:field",
	"sidebar:chair",
	"sidebar:chair:topCount",
	"sidebar:chair:topChairType",
	"sidebar:chair:bottomCount",
	"sidebar:chair:bottomChairType",
	"sidebar:chair:leftChair",
	"sidebar:chair:leftChairType",
	"sidebar:chair:rightChair",
	"sidebar:chair:rightChairType",
	"sidebar:chair:chairCount",
	"sidebar:chair:field",
	"sidebar:area",
	"sidebar:area:width",
	"sidebar:area:depth",
	"sidebar:area:flip",

	"sidebar:table:tableTheme",
	"sidebar:chair:topChairTheme",
	"sidebar:chair:bottomChairTheme",
	"sidebar:chair:leftChairTheme",
	"sidebar:chair:rightChairTheme",
	"sidebar:chair:chairTheme",

	"bottombar",
	"bottombar:theme",
	"bottombar:table",
	"bottombar:chair",
	"bottombar:area",
	"bottombar:chair:topChairType",
	"bottombar:chair:topCount",
	"bottombar:chair:topChairTheme",
	"bottombar:chair:bottomChairType",
	"bottombar:chair:bottomCount",
	"bottombar:chair:bottomChairTheme",
	"bottombar:chair:leftChairType",
	"bottombar:chair:leftCount",
	"bottombar:chair:leftChairTheme",
	"bottombar:chair:rightChairType",
	"bottombar:chair:rightCount",
	"bottombar:chair:rightChairTheme",
	"bottombar:chair:roundChairType",
	"bottombar:chair:roundCount",
	"bottombar:chair:roundChairTheme",
	"bottombar:chair:top",
	"bottombar:chair:bottom",
	"bottombar:chair:left",
	"bottombar:chair:right",
	"bottombar:area:width",
	"bottombar:area:depth",
	"bottombar:area:flip",
] as const;

const themeIncompatibleTermKey = "themeIncompatibleMessage";

export const ui = makeUi({
	slots: {
		sidebar: {
			id: "sidebar",
			type: "fragment",
			children: [
				{
					id: "sidebar:materialTheme",
					type: "thumbnailDropDown",
					state: root.materialTheme,
					showLabel: true,
					appearance: "ghost",
				},
				{
					type: "conditionalMessage",
					state: root.materialThemeDiverged,
					triggerValues: [true],
					termKey: themeIncompatibleTermKey,
				},
				{
					id: "sidebar:accordion-group",
					type: "accordionGroup",
					children: [
						{
							id: "sidebar:table",
							type: "accordion",
							state: root.uiSection,
							children: [
								{
									id: "sidebar:table:field",
									type: "thumbnailDropDown",
									state: root.tableType,
									showLabel: true,
									appearance: "ghost",
								},
								{
									id: "sidebar:table:tableTheme",
									type: "thumbnailDropDown",
									state: root.tableTheme,
									showLabel: false,
									appearance: "ghost",
									hiddenValues: ["theme-none"],
								},
								{
									type: "conditionalMessage",
									state: root.tableTheme,
									triggerValues: ["theme-none"],
									termKey: themeIncompatibleTermKey,
								},
							],
						},
						{
							id: "sidebar:chair",
							type: "accordion",
							state: root.uiSection,
							children: [
								{
									type: "inputGroup",
									children: [
										{
											id: "sidebar:chair:topChairType",
											type: "thumbnailDropDown",
											state: root.chair[SIDE_INDICES.top].chairType,
											visible: root.rectControlsVisible,
											showLabel: true,
											appearance: "ghost",
										},
										{
											id: "sidebar:chair:topCount",
											type: "numberInput",
											state: root.chair[SIDE_INDICES.top].count,
											visible: root.rectControlsVisible,
											showLabel: true,
											appearance: "ghost",
											color: "neutral",
										},
										{
											id: "sidebar:chair:topChairTheme",
											type: "thumbnailDropDown",
											state: root.chair[SIDE_INDICES.top].chairTheme,
											visible: root.rectControlsVisible,
											showLabel: false,
											appearance: "ghost",
											hiddenValues: ["theme-none"],
										},
										{
											type: "conditionalMessage",
											state: root.chair[SIDE_INDICES.top].chairTheme,
											visible: root.rectControlsVisible,
											triggerValues: ["theme-none"],
											termKey: themeIncompatibleTermKey,
										},
									],
								},
								{
									type: "inputGroup",
									children: [
										{
											id: "sidebar:chair:bottomChairType",
											type: "thumbnailDropDown",
											state: root.chair[SIDE_INDICES.bottom].chairType,
											visible: root.rectControlsVisible,
											showLabel: true,
											appearance: "ghost",
										},
										{
											id: "sidebar:chair:bottomCount",
											type: "numberInput",
											state: root.chair[SIDE_INDICES.bottom].count,
											visible: root.rectControlsVisible,
											showLabel: true,
											appearance: "ghost",
											color: "neutral",
										},
										{
											id: "sidebar:chair:bottomChairTheme",
											type: "thumbnailDropDown",
											state: root.chair[SIDE_INDICES.bottom].chairTheme,
											visible: root.rectControlsVisible,
											showLabel: false,
											appearance: "ghost",
											hiddenValues: ["theme-none"],
										},
										{
											type: "conditionalMessage",
											state: root.chair[SIDE_INDICES.bottom].chairTheme,
											visible: root.rectControlsVisible,
											triggerValues: ["theme-none"],
											termKey: themeIncompatibleTermKey,
										},
									],
								},
								{
									type: "inputGroup",
									children: [
										{
											id: "sidebar:chair:leftChairType",
											type: "thumbnailDropDown",
											state: root.chair[SIDE_INDICES.left].chairType,
											visible: root.rectControlsVisible,
											showLabel: true,
											appearance: "ghost",
										},
										{
											id: "sidebar:chair:leftChair",
											type: "numberInput",
											state: root.chair[SIDE_INDICES.left].count,
											visible: root.rectControlsVisible,
											showLabel: true,
											appearance: "ghost",
											color: "neutral",
										},
										{
											id: "sidebar:chair:leftChairTheme",
											type: "thumbnailDropDown",
											state: root.chair[SIDE_INDICES.left].chairTheme,
											visible: root.rectControlsVisible,
											showLabel: false,
											appearance: "ghost",
											hiddenValues: ["theme-none"],
										},
										{
											type: "conditionalMessage",
											state: root.chair[SIDE_INDICES.left].chairTheme,
											visible: root.rectControlsVisible,
											triggerValues: ["theme-none"],
											termKey: themeIncompatibleTermKey,
										},
									],
								},
								{
									type: "inputGroup",
									children: [
										{
											id: "sidebar:chair:rightChairType",
											type: "thumbnailDropDown",
											state: root.chair[SIDE_INDICES.right].chairType,
											visible: root.rectControlsVisible,
											showLabel: true,
											appearance: "ghost",
										},
										{
											id: "sidebar:chair:rightChair",
											type: "numberInput",
											state: root.chair[SIDE_INDICES.right].count,
											visible: root.rectControlsVisible,
											showLabel: true,
											appearance: "ghost",
											color: "neutral",
										},
										{
											id: "sidebar:chair:rightChairTheme",
											type: "thumbnailDropDown",
											state: root.chair[SIDE_INDICES.right].chairTheme,
											visible: root.rectControlsVisible,
											showLabel: false,
											appearance: "ghost",
											hiddenValues: ["theme-none"],
										},
										{
											type: "conditionalMessage",
											state: root.chair[SIDE_INDICES.right].chairTheme,
											visible: root.rectControlsVisible,
											triggerValues: ["theme-none"],
											termKey: themeIncompatibleTermKey,
										},
									],
								},
								{
									type: "inputGroup",
									children: [
										{
											id: "sidebar:chair:field",
											type: "thumbnailDropDown",
											state: root.chair[SIDE_INDICES.round].chairType,
											visible: root.roundControlsVisible,
											showLabel: false,
											appearance: "ghost",
										},
										{
											id: "sidebar:chair:chairCount",
											type: "numberInput",
											state: root.chair[SIDE_INDICES.round].count,
											visible: root.roundControlsVisible,
											showLabel: false,
											appearance: "ghost",
											color: "neutral",
										},
										{
											id: "sidebar:chair:chairTheme",
											type: "thumbnailDropDown",
											state: root.chair[SIDE_INDICES.round].chairTheme,
											visible: root.roundControlsVisible,
											showLabel: false,
											appearance: "ghost",
											hiddenValues: ["theme-none"],
										},
										{
											type: "conditionalMessage",
											state: root.chair[SIDE_INDICES.round].chairTheme,
											visible: root.roundControlsVisible,
											triggerValues: ["theme-none"],
											termKey: themeIncompatibleTermKey,
										},
									],
								},
							],
						},
						{
							id: "sidebar:area",
							type: "accordion",
							state: root.uiSection,
							children: [
								{
									id: "sidebar:area:width",
									type: "decimalInput",
									state: root.areaWidth,
									min: 1,
									max: 25,
								},
								{
									id: "sidebar:area:depth",
									type: "decimalInput",
									state: root.areaDepth,
									min: 1,
									max: 25,
								},
								{
									id: "sidebar:area:flip",
									type: "button",
									state: root.flipArea,
									size: "default",
									appearance: "ghost",
									color: "neutral",
									showLabel: true,
									events: {
										click: {
											type: "setProperty",
											property: root.flipArea,
											value: true,
										},
									},
								},
							],
						},
					],
				},
			],
		},
		bottomBar: {
			id: "bottombar",
			type: "tabFrame",
			state: root.uiSection,
			children: [
				{
					id: "bottombar:theme",
					type: "fragment",
					children: [
						{
							type: "horizontalCardSelect",
							state: root.materialTheme,
							showLabel: false,
							padImages: true,
						},
						{
							type: "conditionalMessage",
							state: root.materialThemeDiverged,
							triggerValues: [true],
							termKey: themeIncompatibleTermKey,
						},
					],
				},
				{
					id: "bottombar:table",
					type: "fragment",
					children: [
						{
							type: "horizontalCardSelect",
							state: root.tableType,
						},
						{
							type: "horizontalCardSelect",
							state: root.tableTheme,
							hiddenValues: ["theme-none"],
							padImages: true,
						},
						{
							type: "conditionalMessage",
							state: root.tableTheme,
							triggerValues: ["theme-none"],
							termKey: themeIncompatibleTermKey,
						},
					],
				},
				{
					id: "bottombar:chair",
					type: "fragment",
					children: [
						// --- Rect table: per-side sub-tabs ---
						{
							type: "visibleGroup",
							visible: root.rectControlsVisible,
							children: [
								{
									type: "tabFrame",
									children: [
										{
											id: "bottombar:chair:top",
											type: "fragment",
											children: [
												{
													id: "bottombar:chair:topChairType",
													type: "horizontalCardSelect",
													state: root.chair[SIDE_INDICES.top].chairType,
												},
												{
													id: "bottombar:chair:topCount",
													type: "numberInput",
													state: root.chair[SIDE_INDICES.top].count,
													showLabel: true,
													appearance: "ghost",
													color: "neutral",
												},
												{
													id: "bottombar:chair:topChairTheme",
													type: "horizontalCardSelect",
													state: root.chair[SIDE_INDICES.top].chairTheme,
													hiddenValues: ["theme-none"],
													padImages: true,
												},
												{
													type: "conditionalMessage",
													state: root.chair[SIDE_INDICES.top].chairTheme,
													triggerValues: ["theme-none"],
													termKey: themeIncompatibleTermKey,
												},
											],
										},
										{
											id: "bottombar:chair:bottom",
											type: "fragment",
											children: [
												{
													id: "bottombar:chair:bottomChairType",
													type: "horizontalCardSelect",
													state: root.chair[SIDE_INDICES.bottom].chairType,
												},
												{
													id: "bottombar:chair:bottomCount",
													type: "numberInput",
													state: root.chair[SIDE_INDICES.bottom].count,
													showLabel: true,
													appearance: "ghost",
													color: "neutral",
												},
												{
													id: "bottombar:chair:bottomChairTheme",
													type: "horizontalCardSelect",
													state: root.chair[SIDE_INDICES.bottom].chairTheme,
													hiddenValues: ["theme-none"],
													padImages: true,
												},
												{
													type: "conditionalMessage",
													state: root.chair[SIDE_INDICES.bottom].chairTheme,
													triggerValues: ["theme-none"],
													termKey: themeIncompatibleTermKey,
												},
											],
										},
										{
											id: "bottombar:chair:left",
											type: "fragment",
											children: [
												{
													id: "bottombar:chair:leftChairType",
													type: "horizontalCardSelect",
													state: root.chair[SIDE_INDICES.left].chairType,
												},
												{
													id: "bottombar:chair:leftCount",
													type: "numberInput",
													state: root.chair[SIDE_INDICES.left].count,
													showLabel: true,
													appearance: "ghost",
													color: "neutral",
												},
												{
													id: "bottombar:chair:leftChairTheme",
													type: "horizontalCardSelect",
													state: root.chair[SIDE_INDICES.left].chairTheme,
													hiddenValues: ["theme-none"],
													padImages: true,
												},
												{
													type: "conditionalMessage",
													state: root.chair[SIDE_INDICES.left].chairTheme,
													triggerValues: ["theme-none"],
													termKey: themeIncompatibleTermKey,
												},
											],
										},
										{
											id: "bottombar:chair:right",
											type: "fragment",
											children: [
												{
													id: "bottombar:chair:rightChairType",
													type: "horizontalCardSelect",
													state: root.chair[SIDE_INDICES.right].chairType,
												},
												{
													id: "bottombar:chair:rightCount",
													type: "numberInput",
													state: root.chair[SIDE_INDICES.right].count,
													showLabel: true,
													appearance: "ghost",
													color: "neutral",
												},
												{
													id: "bottombar:chair:rightChairTheme",
													type: "horizontalCardSelect",
													state: root.chair[SIDE_INDICES.right].chairTheme,
													hiddenValues: ["theme-none"],
													padImages: true,
												},
												{
													type: "conditionalMessage",
													state: root.chair[SIDE_INDICES.right].chairTheme,
													triggerValues: ["theme-none"],
													termKey: themeIncompatibleTermKey,
												},
											],
										},
									],
								},
							],
						},
						// --- Round table: flat layout ---
						{
							type: "visibleGroup",
							visible: root.roundControlsVisible,
							children: [
								{
									id: "bottombar:chair:roundChairType",
									type: "horizontalCardSelect",
									state: root.chair[SIDE_INDICES.round].chairType,
								},
								{
									id: "bottombar:chair:roundCount",
									type: "numberInput",
									state: root.chair[SIDE_INDICES.round].count,
									appearance: "ghost",
									color: "neutral",
								},
								{
									id: "bottombar:chair:roundChairTheme",
									type: "horizontalCardSelect",
									state: root.chair[SIDE_INDICES.round].chairTheme,
									hiddenValues: ["theme-none"],
									padImages: true,
								},
								{
									type: "conditionalMessage",
									state: root.chair[SIDE_INDICES.round].chairTheme,
									triggerValues: ["theme-none"],
									termKey: themeIncompatibleTermKey,
								},
							],
						},
					],
				},
				{
					id: "bottombar:area",
					type: "fragment",
					children: [
						{
							id: "bottombar:area:width",
							type: "decimalInput",
							state: root.areaWidth,
							min: 1,
							max: 25,
						},
						{
							id: "bottombar:area:depth",
							type: "decimalInput",
							state: root.areaDepth,
							min: 1,
							max: 25,
						},
						{
							id: "bottombar:area:flip",
							type: "button",
							state: root.flipArea,
							size: "default",
							appearance: "ghost",
							color: "neutral",
							showLabel: true,
							events: {
								click: {
									type: "setProperty",
									property: root.flipArea,
									value: true,
								},
							},
						},
					],
				},
			],
		},
		wizard: {
			type: "fragment",
		},
		productPicker: {
			type: "fragment",
		},
		productInfo: {
			type: "fragment",
		},
		cart: {
			type: "fragment",
		},
	},
	favicons: [
		{ type: "image/png", image: "images/favicon/favicon-16x16.png", size: 16 },
		{ type: "image/png", image: "images/favicon/favicon-32x32.png", size: 32 },
		{
			type: "image/png",
			image: "images/favicon/favicon-150x150.png",
			size: 150,
		},
		{
			type: "image/png",
			image: "images/favicon/favicon-192x192.png",
			size: 192,
		},
	],
	logos: { configurator: { desktop: "images/logo/logo.svg" }, overlay: {} },
	settings: {
		layout: "sidebar",
		cartColumnVisibility: {
			category: true,
			price: true,
			quantity: true,
			total: true,
		},
	},
} satisfies UiStructure);
